import { useMemo, useState } from "react";
import type { DetectionStatus, MeetingDetectionDTO, MeetingType } from "../api/types";
import { MEETING_TYPE_LABEL, avatarColor, formatClock } from "../utils/format";

interface MeetingTableProps {
  detections: MeetingDetectionDTO[];
  /** 部门过滤选项（管理员=全部部门，负责人=本部门） */
  deptOptions: { id: string; name: string }[];
  /** 是否允许人工修正（ADMIN / DEPT_LEAD） */
  canOverride: boolean;
  onOverride: (detection: MeetingDetectionDTO, status: DetectionStatus) => void;
}

const STATUS_TAG: Record<DetectionStatus, { cls: string; text: string }> = {
  DONE: { cls: "tag-success", text: "已开启" },
  MISSED: { cls: "tag-danger", text: "未开启" },
  IN_PROGRESS: { cls: "tag-warn", text: "即将开始" },
  UPCOMING: { cls: "tag-gray", text: "未到时间" },
};

const TYPE_TAG: Record<MeetingType, string> = {
  DAILY: "tag-info",
  WEEKLY: "tag-teal",
  MONTHLY: "tag-purple",
};

const TYPE_FILTERS: { value: "all" | MeetingType; label: string }[] = [
  { value: "all", label: "全部" },
  { value: "DAILY", label: "日会" },
  { value: "WEEKLY", label: "周会" },
  { value: "MONTHLY", label: "月会" },
];

export function MeetingTable({ detections, deptOptions, canOverride, onOverride }: MeetingTableProps) {
  const [typeFilter, setTypeFilter] = useState<"all" | MeetingType>("all");
  const [deptFilter, setDeptFilter] = useState("all");

  const rows = useMemo(
    () =>
      detections.filter(
        (d) =>
          (typeFilter === "all" || d.type === typeFilter) &&
          (deptFilter === "all" || d.deptId === deptFilter),
      ),
    [detections, typeFilter, deptFilter],
  );

  return (
    <section className="panel" id="secDetect">
      <div className="panel-head">
        <div className="panel-title">周期性会议开启检测</div>
        <div className="table-tools">
          <select
            value={deptFilter}
            onChange={(e) => setDeptFilter(e.target.value)}
            aria-label="按部门筛选"
          >
            <option value="all">全部部门</option>
            {deptOptions.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
          <div className="tabs" role="tablist" aria-label="按会议类型筛选">
            {TYPE_FILTERS.map((t) => (
              <button
                key={t.value}
                role="tab"
                aria-selected={typeFilter === t.value}
                className={`tab${typeFilter === t.value ? " active" : ""}`}
                onClick={() => setTypeFilter(t.value)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="panel-body flush">
        <table>
          <thead>
            <tr>
              <th>部门</th>
              <th>类型</th>
              <th>周期</th>
              <th>应开时间</th>
              <th>主持人</th>
              <th>状态</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr className="empty-row">
                <td colSpan={6} className="empty-cell">
                  没有符合条件的会议
                </td>
              </tr>
            ) : (
              rows.map((m) => {
                const s = STATUS_TAG[m.status];
                const statusText =
                  m.status === "DONE" && m.actualAt ? `已开启 ${formatClock(m.actualAt)}` : s.text;
                return (
                  <tr key={m.id}>
                    <td data-label="部门">
                      <b>{m.deptName}</b>
                      {m.title && (
                        <div className="cell-sub" title={m.title}>
                          {m.title}
                        </div>
                      )}
                    </td>
                    <td data-label="类型">
                      <span className={`tag ${TYPE_TAG[m.type]}`}>{MEETING_TYPE_LABEL[m.type] ?? m.type}</span>
                    </td>
                    <td data-label="周期" className="td-muted">
                      {m.cycle}
                    </td>
                    <td data-label="应开时间">{formatClock(m.expectedAt)}</td>
                    <td data-label="主持人">
                      <div className="host-cell">
                        <div className="avatar" style={{ background: avatarColor(m.hostName ?? m.deptName) }}>
                          {(m.hostName ?? m.deptName)[0]}
                        </div>
                        <span>{m.hostName ?? "未指定"}</span>
                      </div>
                    </td>
                    <td data-label="状态">
                      {canOverride ? (
                        <select
                          className="tag tag-select"
                          value={m.status}
                          onChange={(e) => onOverride(m, e.target.value as DetectionStatus)}
                          aria-label={`修正 ${m.deptName} 会议状态`}
                        >
                          <option value="DONE">已开启</option>
                          <option value="MISSED">未开启</option>
                          <option value="IN_PROGRESS">即将开始</option>
                          <option value="UPCOMING">未到时间</option>
                        </select>
                      ) : (
                        <span className={`tag ${s.cls}`}>{statusText}</span>
                      )}
                      {m.source === "manual" && (
                        <span className="tag tag-gray" title="该状态由人工修正">
                          人工
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
